import { bootWebContainer, waitForTemplateReady } from './index';
import { detectWorkbenchCapability } from './capability';
import { workbenchStore } from '../stores/workbench';

export type PrewarmOptions = {
  signal?: AbortSignal;
};

/**
 * Boot WebContainer + mount template during browser idle time.
 * Shares the global singleton — later real boots are cache hits.
 */
export async function prewarmWorkbench(opts?: PrewarmOptions): Promise<void> {
  if (typeof window === 'undefined' || !window.crossOriginIsolated) {
    workbenchStore.prewarmState.set('skipped');
    return;
  }
  if (opts?.signal?.aborted) {
    workbenchStore.prewarmState.set('skipped');
    return;
  }

  workbenchStore.prewarmState.set('running');
  try {
    // Never take the workbench away from another tab just to warm this one up.
    await bootWebContainer({ takeOverIdleTab: false });
    if (opts?.signal?.aborted) {
      workbenchStore.prewarmState.set('skipped');
      return;
    }
    await waitForTemplateReady();
    workbenchStore.prewarmState.set('ready');
  } catch (err) {
    console.warn('[Workbench] prewarm failed', err);
    workbenchStore.prewarmState.set('failed');
  }
}

/**
 * Prewarm holds hundreds of MB before the founder asks for a prototype, so only the `full`
 * device tier (desktop Chromium, 8 GB+, OPFS; see `./capability.ts`) prewarms. An unknown
 * `deviceMemory` is never `full`.
 */
export function shouldSchedulePrewarm(): boolean {
  if (typeof window === 'undefined' || !window.crossOriginIsolated) return false;

  const envFlag = import.meta.env?.VITE_WORKBENCH_PREWARM;
  if (envFlag === '0' || envFlag === 'false') return false;

  const conn = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;
  if (conn?.saveData) return false;
  if (conn?.effectiveType === 'slow-2g' || conn?.effectiveType === '2g') return false;

  return detectWorkbenchCapability().tier === 'full';
}
